import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import api, { formatDateTime, formatPhone } from '../services/api';
import AssignStylistModal from '../components/AssignStylistModal';
import BookingModal from '../components/BookingModal';
import BookingPanel from '../components/BookingPanel';
import MultiBookingModal from '../components/MultiBookingModal';
import ChatModal from '../components/ChatModal';
import RecordingModal from '../components/RecordingModal';
import { BranchTag, useEngagementScope } from '../context/EngagementScope';
import {
  Confirm,
  ChipRow,
  DESK_COLOR,
  DateRange,
  DetailRow,
  DownloadIcon,
  Loader,
  Modal,
  NoShowTag,
  Pagination,
  RefreshIcon,
  SearchBox,
  SourceBadge,
  Spinner,
  UnassignedPill,
  ViewButton,
  WelcomeBar,
  exportCSV,
  inDateRange,
  usePagination,
} from '../components/ui';

const STATUS_CHIPS = [
  { key: '', label: 'All' },
  { key: 'booked', label: 'Booked' },
  { key: 'service_taken', label: 'Service taken' },
  { key: 'no_show', label: 'No-show' },
  { key: 'not_taken', label: 'Visited, not taken' },
];
const SOURCE_CHIPS = [
  { key: '', label: 'All' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'voice', label: 'Voice' },
  { key: 'walk_in', label: 'Walk-in / desk' },
  { key: 'checkin', label: 'Salon visit' },
];
const ADDED_CHIPS = [
  { key: '', label: 'All' },
  { key: 'yes', label: 'Added in salon' },
];
const STYLIST_CHIPS = [
  { key: '', label: 'All' },
  { key: 'assigned', label: 'With stylist' },
  { key: 'unassigned', label: 'Unassigned' },
];
const STATUS_LABEL = { booked: 'Booked', service_taken: 'Service taken', no_show: 'No-show', not_taken: 'Visited, not taken' };
const REASONS = { price: 'Price', time: 'Time', changed_mind: 'Changed my mind', other: 'Other' };
const CANCELLED_LABEL = 'Cancelled';

/** What happens on WhatsApp when the status changes. */
function statusEffect(from, to) {
  const lines = [];
  if (to === 'service_taken') {
    lines.push('The feedback message is sent now and the return-visit reminders are scheduled from today.');
    if (from === 'booked') lines.push('Appointment reminders still waiting are cancelled.');
  }
  if (to === 'no_show') {
    if (from === 'booked') lines.push('Appointment reminders still waiting are cancelled.');
    lines.push(
      'The missed-appointment message goes out later, asking if they would like a new time (if it is turned on and the appointment is from the last 2 days). It stops if they reply or book.',
    );
  }
  if (from === 'no_show') lines.push('Missed-appointment messages not yet sent are cancelled.');
  if (to === 'not_taken') {
    if (from === 'booked') lines.push('Appointment reminders still waiting are cancelled.');
    lines.push('They came but did not take this service. The reason is saved; if the “Visited, no service” automation is on, it asks them why and later offers a better option.');
  }
  if (to === 'booked') lines.push('Appointment reminders that are still ahead are scheduled again. No second confirmation is sent.');
  if (from === 'service_taken') {
    lines.push('Return-visit messages not yet sent are cancelled. A feedback message already sent cannot be taken back.');
  }
  return lines.join(' ');
}

/** "Did they come?": appointments still Booked well after their time, with one-tap answers. */
function NeedsUpdate({ rows, busy, onMark }) {
  const [showAll, setShowAll] = useState(false);
  if (!rows.length) return null;
  const shown = showAll ? rows : rows.slice(0, 5);
  return (
    <div className="de-needs-update">
      <div className="de-row de-row--between de-row--wrap" style={{ gap: 8 }}>
        <div className="de-strong">
          🕒 Did they come? <span className="de-pill de-pill--warn de-pill--sm">{rows.length}</span>
        </div>
        <div className="de-small de-muted">Still Booked after their time. Mark each one so reminders and follow-up messages stay right.</div>
      </div>
      {shown.map((r) => (
        <div key={r.id} className="de-needs-row">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="de-row de-row--wrap" style={{ gap: 6, alignItems: 'center' }}>
              <span className="de-strong">{r.customer_name || formatPhone(r.customer_phone) || 'Customer'}</span>
              <span className="de-muted">· {r.service_name}</span>
              <NoShowTag count={r.customer_no_shows} />
            </div>
            <div className="de-small de-muted">
              {formatDateTime(r.starts_at)} · {r.staff_name || 'Any available stylist'}
            </div>
          </div>
          <div className="de-row" style={{ gap: 8, flexShrink: 0 }}>
            <button type="button" className="de-btn de-btn--success-solid de-btn--xs" disabled={lineBusy(busy, r)} onClick={() => onMark(r, 'service_taken')}>
              ✅ Came
            </button>
            <button type="button" className="de-btn de-btn--danger-solid de-btn--xs" disabled={lineBusy(busy, r)} onClick={() => onMark(r, 'no_show')}>
              ❌ No-show
            </button>
          </div>
        </div>
      ))}
      {rows.length > 5 ? (
        <button type="button" className="de-link-btn" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      ) : null}
    </div>
  );
}

function StatusSelect({ row, busy, onChange }) {
  return (
    <select
      className={`de-status-select de-status-select--${row.status}`}
      value={row.status}
      disabled={busy}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(row, e.target.value)}
      title="Change status"
      aria-label="Appointment status"
    >
      {Object.entries(STATUS_LABEL).map(([key, label]) => (
        <option key={key} value={key}>
          {label}
        </option>
      ))}
    </select>
  );
}

/** Group service lines into bookings. A line with no booking_id stands alone. */
function groupBookings(lines) {
  const map = new Map();
  lines.forEach((line) => {
    const key = line.booking_id ? String(line.booking_id) : `line:${line.id}`;
    let group = map.get(key);
    if (!group) {
      group = { key, bookingId: line.booking_id || null, lines: [] };
      map.set(key, group);
    }
    group.lines.push(line);
  });
  const groups = Array.from(map.values());
  groups.forEach((group) => {
    group.lines.sort((a, b) => {
      const byNo = (Number(a.line_no) || 0) - (Number(b.line_no) || 0);
      if (byNo) return byNo;
      return String(a.starts_at || '').localeCompare(String(b.starts_at || ''));
    });
    group.startsAt = group.lines.reduce((min, line) => {
      if (line.starts_at == null || line.starts_at === '') return min;
      if (min == null || String(line.starts_at) < String(min)) return line.starts_at;
      return min;
    }, null);
  });
  // Latest booking first (earliest start among its lines).
  groups.sort((a, b) => String(b.startsAt || '').localeCompare(String(a.startsAt || '')));
  return groups;
}

function lineBusy(busy, row) {
  return busy === row.id || busy === `booking:${row.booking_id}` || busy === `booking:line:${row.id}`;
}

function BookingActions({ bookingId, canNoShow, disabled, onCancel, onNoShow }) {
  if (!bookingId && !canNoShow) return null;
  return (
    <select
      className="de-select"
      defaultValue=""
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        const action = e.target.value;
        e.target.value = '';
        if (action === 'cancel') onCancel();
        if (action === 'no_show') onNoShow();
      }}
      aria-label="Booking actions"
      title="Booking actions"
    >
      <option value="">Actions</option>
      {bookingId ? <option value="cancel">Cancel booking</option> : null}
      {canNoShow ? <option value="no_show">No-show whole booking</option> : null}
    </select>
  );
}

const CSV_COLUMNS = [
  { label: 'Customer Name', key: 'customer_name' },
  { label: 'Phone', key: 'customer_phone' },
  { label: 'Service', key: 'service_name' },
  { label: 'Assigned Staff', value: (r) => r.staff_name || 'Unassigned' },
  { label: 'Source', value: (r) => (r.source === 'sarvam' || r.source === 'voice' ? 'Voice call' : r.source === 'walk_in' ? 'Walk-in / desk' : r.source === 'checkin' ? 'Salon visit' : r.source === 'instagram' ? 'Instagram' : 'WhatsApp') },
  { label: 'First Activity', key: 'first_activity_at' },
  { label: 'Date & Time', key: 'starts_at' },
  { label: 'Status', key: 'status' },
];

export default function Appointments() {
  const { multiBranch } = useEngagementScope();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [addedInSalon, setAddedInSalon] = useState('');
  const [stylist, setStylist] = useState('');
  const [openGroups, setOpenGroups] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState(null);
  const [chatFor, setChatFor] = useState(null);
  const [chatChannel, setChatChannel] = useState('whatsapp');
  const [recording, setRecording] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [reason, setReason] = useState('');
  const [booking, setBooking] = useState(null);
  const [multiBooking, setMultiBooking] = useState(null); // prefill from the booking form
  const [assignFor, setAssignFor] = useState(null);
  const [params, setParams] = useSearchParams();

  // Opened from the presentation page's Booking tile (?book=walk_in or ?book=later): open the form.
  useEffect(() => {
    const mode = params.get('book');
    if (mode === 'walk_in' || mode === 'later') {
      setBooking({ mode });
      params.delete('book');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const load = (silent) => {
    if (!silent) setLoading(true);
    return api
      .listAppointments()
      .then((data) => {
        setRows(data);
        setDetail((d) => (d ? data.find((r) => r.id === d.id) || null : d));
      })
      .catch((e) => toast.error(e.message || 'Failed to load appointments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(false);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.status !== status) return false;
      if (source === 'voice' && r.source !== 'voice' && r.source !== 'sarvam') return false;
      if (source && source !== 'voice' && r.source !== source) return false;
      if (addedInSalon && !r.added_in_salon) return false;
      if (stylist === 'assigned' && !r.staff_name) return false;
      if (stylist === 'unassigned' && r.staff_name) return false;
      if (!inDateRange(r.starts_at, dateFrom, dateTo)) return false;
      if (!q) return true;
      return [r.customer_name, r.customer_phone, r.service_name, r.staff_name].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, search, status, source, addedInSalon, stylist, dateFrom, dateTo]);

  const bookings = useMemo(() => groupBookings(filtered), [filtered]);
  const { page, setPage, pageSize, setPageSize, paginated, totalPages, offset } = usePagination(bookings);
  // Services per booking (a booking with several services shows "2 of 3" on each line).
  const bookingSizes = useMemo(() => {
    const out = {};
    rows.forEach((r) => {
      if (r.booking_id && r.status !== 'cancelled') out[r.booking_id] = (out[r.booking_id] || 0) + 1;
    });
    return out;
  }, [rows]);
  const needsUpdate = useMemo(
    () => rows.filter((r) => r.needs_update).sort((a, b) => String(b.starts_at).localeCompare(String(a.starts_at))),
    [rows],
  );

  // Status change: ask first, because Service taken sends WhatsApp messages.
  const requestStatus = (row, next) => {
    if (!next || next === row.status) return;
    setReason(row.not_taken_reason || '');
    setPendingStatus({ row, next });
  };

  const applyStatus = async () => {
    const { row, next } = pendingStatus;
    setPendingStatus(null);
    setBusy(row.id);
    try {
      const res = await api.setAppointmentStatus(row.id, next, next === 'not_taken' ? reason : '');
      toast.success(`${row.customer_name || 'Appointment'}: ${STATUS_LABEL[next]}. ${(res.notes || []).join('. ')}`);
      await load(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const openConversation = (row) => {
    if (row.source === 'sarvam' || row.source === 'voice') {
      setRecording({ ...(row.call || {}), customer_name: row.customer_name, customer_phone: row.customer_phone, service_name: row.service_name, starts_at: row.starts_at, staff_name: row.staff_name });
    } else {
      setChatChannel(row.source === 'instagram' ? 'instagram' : 'whatsapp');
      setChatFor(row.customer_id);
    }
  };

  // Open until staff collapse it, so a one-service booking still shows its status menu.
  const groupOpen = (group) => (Object.prototype.hasOwnProperty.call(openGroups, group.key) ? openGroups[group.key] : true);

  const toggleGroup = (group) => {
    setOpenGroups((prev) => {
      const open = Object.prototype.hasOwnProperty.call(prev, group.key) ? prev[group.key] : true;
      return { ...prev, [group.key]: !open };
    });
  };

  // Booked lines for a whole booking, including ones hidden by the current filter.
  const bookedInBooking = (group) => {
    if (!group.bookingId) return group.lines.filter((line) => line.status === 'booked');
    const id = String(group.bookingId);
    return rows.filter((line) => String(line.booking_id) === id && line.status === 'booked');
  };

  const cancelWhole = async (group) => {
    if (!group.bookingId) return;
    const why = window.prompt('Reason for cancelling this booking');
    if (why === null) return;
    setBusy(`booking:${group.key}`);
    try {
      await api.cancelBooking(group.bookingId, why.trim());
      toast.success('Booking cancelled');
      await load(true);
    } catch (e) {
      toast.error(e.message || 'Could not cancel booking');
    } finally {
      setBusy(null);
    }
  };

  const noShowWhole = async (group) => {
    const targets = bookedInBooking(group);
    if (!targets.length) return;
    setBusy(`booking:${group.key}`);
    let failed = null;
    try {
      for (const line of targets) {
        try {
          await api.setAppointmentStatus(line.id, 'no_show', '');
        } catch (e) {
          failed = e;
        }
      }
      if (failed) toast.error(failed.message || 'Could not mark every service no-show');
      else toast.success(targets.length === 1 ? 'Marked no-show' : `Marked ${targets.length} services as no-show`);
      await load(true);
    } finally {
      setBusy(null);
    }
  };

  const doExport = () => {
    if (!filtered.length) {
      toast.error('No appointments to export');
      return;
    }
    exportCSV(filtered, CSV_COLUMNS, 'appointments');
    toast.success(`Exported ${filtered.length} appointments`);
  };

  return (
    <div className="de-fade">
      <WelcomeBar sub="Bookings from WhatsApp, voice calls and the desk. Any-stylist bookings wait here until you assign a stylist." />

      <div className="de-panel">
        <div className="de-panel-title">Appointments</div>

        <div className="de-toolbar">
          <SearchBox value={search} onChange={setSearch} placeholder="Search name, phone, service or stylist" />
          <button type="button" className="de-chip" onClick={() => load(false)} title="Refresh">
            <span className="de-row" style={{ gap: 6 }}>
              <RefreshIcon /> Refresh
            </span>
          </button>
          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={doExport}>
            <DownloadIcon /> Excel
          </button>
          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setBooking({ mode: 'later' })}>
            + Book appointment
          </button>
          <button type="button" className="de-btn de-btn--primary de-btn--sm" onClick={() => setBooking({ mode: 'walk_in' })}>
            🚶 Walk-in
          </button>
        </div>

        <NeedsUpdate rows={needsUpdate} busy={busy} onMark={requestStatus} />

        <DateRange
          from={dateFrom}
          to={dateTo}
          onFrom={setDateFrom}
          onTo={setDateTo}
          onClear={() => {
            setDateFrom('');
            setDateTo('');
          }}
        />
        <ChipRow label="Status" chips={STATUS_CHIPS} value={status} onChange={setStatus} />
        <ChipRow label="Source" chips={SOURCE_CHIPS} value={source} onChange={setSource} />
        <ChipRow label="Salon" chips={ADDED_CHIPS} value={addedInSalon} onChange={setAddedInSalon} />
        <ChipRow label="Stylist" chips={STYLIST_CHIPS} value={stylist} onChange={setStylist} />

        <div className="de-count-line">
          {bookings.length} booking{bookings.length === 1 ? '' : 's'} · {filtered.length} service{filtered.length === 1 ? '' : 's'}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="de-table-shell">
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    {['S.No', 'Customer Name', 'Phone', 'Service', 'Assigned Staff', ...(multiBranch ? ['Branch'] : []), 'Source', 'First Activity', 'Date & Time', 'Status', 'Action'].map(
                      (h) => (
                        <th key={h}>{h}</th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={multiBranch ? 11 : 10} className="de-table-empty">
                        No appointments found
                      </td>
                    </tr>
                  ) : (
                    paginated.map((group, i) => {
                      const first = group.lines[0];
                      const open = groupOpen(group);
                      const names = group.lines.map((line) => line.service_name).filter(Boolean);
                      const groupBusy = busy === `booking:${group.key}` || group.lines.some((line) => busy === line.id);
                      const canNoShow = bookedInBooking(group).length > 0;
                      return (
                        <React.Fragment key={group.key}>
                          <tr onClick={(e) => e.stopPropagation()}>
                            <td className="de-td-muted" style={{ whiteSpace: 'nowrap' }}>
                              <button
                                type="button"
                                className="de-link-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroup(group);
                                }}
                                aria-expanded={open}
                                aria-label={open ? 'Collapse booking' : 'Expand booking'}
                                title={open ? 'Hide services' : 'Show services'}
                              >
                                {open ? '▾' : '▸'}
                              </button>{' '}
                              {offset + i + 1}
                            </td>
                            <td>
                              <div className="de-td-strong">{first.customer_name || '—'}</div>
                              {first.contact_name && first.contact_name !== first.customer_name ? <div className="de-td-muted">via {first.contact_name}</div> : null}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>{formatPhone(first.customer_phone)}</td>
                            <td>
                              <div className="de-td-strong">
                                {group.lines.length} service{group.lines.length === 1 ? '' : 's'}
                              </div>
                              {names.length ? <div className="de-td-muted de-small">{names.join(', ')}</div> : null}
                            </td>
                            <td />
                            {multiBranch ? (
                              <td>
                                <BranchTag franchiseId={first.franchise_id} name={first.branch_name} />
                              </td>
                            ) : null}
                            <td>
                              <SourceBadge source={first.source} />
                            </td>
                            <td />
                            <td style={{ whiteSpace: 'nowrap' }}>{group.startsAt ? formatDateTime(group.startsAt) : '—'}</td>
                            <td />
                            <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                              <BookingActions
                                bookingId={group.bookingId}
                                canNoShow={canNoShow}
                                disabled={!!busy}
                                onCancel={() => cancelWhole(group)}
                                onNoShow={() => noShowWhole(group)}
                              />
                            </td>
                          </tr>
                          {open
                            ? group.lines.map((r) => (
                                <tr key={r.id} className="de-row-click" onClick={() => setDetail(r)}>
                                  <td className="de-td-muted" style={{ boxShadow: `inset 3px 0 0 ${DESK_COLOR}` }} />
                                  <td style={{ paddingLeft: 20 }}>
                                    <div className="de-td-strong">{r.customer_name || '—'}</div>
                                    {r.contact_name && r.contact_name !== r.customer_name ? <div className="de-td-muted">via {r.contact_name}</div> : null}
                                  </td>
                                  <td style={{ whiteSpace: 'nowrap' }}>{formatPhone(r.customer_phone)}</td>
                                  <td style={{ whiteSpace: 'nowrap', paddingLeft: 20 }}>
                                    {r.service_name}
                                    {r.service_price ? <div className="de-td-muted de-small">{r.service_price}</div> : null}
                                    <div className="de-row" style={{ gap: 4, flexWrap: 'wrap' }}>
                                      {bookingSizes[r.booking_id] > 1 ? (
                                        <span className="de-pill de-pill--sm" title="One booking with several services">
                                          {r.line_no} of {bookingSizes[r.booking_id]}
                                        </span>
                                      ) : null}
                                      {r.added_in_salon ? <span className="de-pill de-pill--sm de-pill--info">Added in salon</span> : null}
                                      {r.extra_slots ? <span className="de-pill de-pill--sm">+{r.extra_slots} slot</span> : null}
                                      {r.pending_change ? <span className="de-pill de-pill--sm de-pill--warn">Waiting for reply</span> : null}
                                    </div>
                                  </td>
                                  <td style={{ whiteSpace: 'nowrap' }} onClick={(e) => r.status === 'booked' && !r.staff_name && e.stopPropagation()}>
                                    {r.staff_name ? (
                                      r.staff_name
                                    ) : r.status === 'booked' ? (
                                      <button type="button" className="de-assign-btn" onClick={() => setAssignFor(r.id)} title="Give this booking a stylist">
                                        ⏳ Assign stylist
                                      </button>
                                    ) : (
                                      <UnassignedPill />
                                    )}
                                  </td>
                                  {multiBranch ? (
                                    <td>
                                      <BranchTag franchiseId={r.franchise_id} name={r.branch_name} />
                                    </td>
                                  ) : null}
                                  <td>
                                    <SourceBadge source={r.source} />
                                  </td>
                                  <td className="de-td-muted" style={{ minWidth: 110 }}>
                                    {formatDateTime(r.first_activity_at)}
                                  </td>
                                  <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.starts_at)}</td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    {r.status === 'cancelled' ? (
                                      <span className="de-pill de-pill--muted">{CANCELLED_LABEL}</span>
                                    ) : (
                                      <StatusSelect row={r} busy={groupBusy || lineBusy(busy, r)} onChange={requestStatus} />
                                    )}
                                    {r.status === 'no_show' && r.no_show_auto ? <div className="de-td-muted de-small">Auto at closing</div> : null}
                                    {r.visit_label ? (
                                      <div className="de-small" style={{ color: r.visit_status === 'cancelled' ? 'var(--de-warn)' : DESK_COLOR }} title="Checked in at the salon desk from this booking">
                                        {r.visit_label}
                                        {r.visit_services?.length ? ` · ${r.visit_services.join(', ')}` : ''}
                                        {r.visit_stylist ? ` · ${r.visit_stylist}` : ''}
                                      </div>
                                    ) : null}
                                    {r.needs_update ? <div className="de-small" style={{ color: 'var(--de-warn)' }}>Did they come?</div> : null}
                                    {r.status === 'not_taken' && r.not_taken_reason ? <div className="de-td-muted de-small">Reason: {REASONS[r.not_taken_reason] || r.not_taken_reason}</div> : null}
                                  </td>
                                  <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                                    <ViewButton onClick={() => openConversation(r)} title={r.source === 'sarvam' || r.source === 'voice' ? 'Play call recording' : r.source === 'instagram' ? 'View Instagram chat' : 'View WhatsApp chat'} />
                                  </td>
                                </tr>
                              ))
                            : null}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <Pagination attached page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} />
          </div>
        )}
      </div>

      {detail ? (
        <Modal title="Appointment details" onClose={() => setDetail(null)} size="wide">
          <DetailRow label="Booked for">{detail.customer_name || '—'}</DetailRow>
          {detail.contact_name && detail.contact_name !== detail.customer_name ? (
            <DetailRow label="Contact">{detail.contact_name} (owner of this number)</DetailRow>
          ) : null}
          <DetailRow label="Phone">{formatPhone(detail.customer_phone)}</DetailRow>
          <DetailRow label="Service">
            {detail.service_name}
            {detail.service_price ? <span className="de-muted"> · {detail.service_price}</span> : null}
          </DetailRow>
          {detail.branch_name ? <DetailRow label="Branch">{detail.branch_name}</DetailRow> : null}
          <DetailRow label="Assigned">
            <span className="de-row" style={{ gap: 8, alignItems: 'center' }}>
              {detail.staff_name || <UnassignedPill />}
              {detail.status === 'booked' ? (
                <button type="button" className="de-btn de-btn--outline de-btn--xs" onClick={() => setAssignFor(detail.id)}>
                  {detail.staff_name ? 'Change stylist or time' : 'Assign stylist'}
                </button>
              ) : null}
            </span>
          </DetailRow>
          <DetailRow label="Source">
            <SourceBadge source={detail.source} showLabel />
          </DetailRow>
          <DetailRow label="First activity">{formatDateTime(detail.first_activity_at)}</DetailRow>
          <DetailRow label="Date & time">{formatDateTime(detail.starts_at)}</DetailRow>
          <DetailRow label="Status">
            <StatusSelect row={detail} busy={lineBusy(busy, detail)} onChange={requestStatus} />
          </DetailRow>
          {detail.service_taken_at ? <DetailRow label="Service taken at">{formatDateTime(detail.service_taken_at)}</DetailRow> : null}
          {detail.no_show_at ? (
            <DetailRow label="Marked No-show">
              {formatDateTime(detail.no_show_at)}
              {detail.no_show_auto ? ' (automatically at closing time)' : ''}
            </DetailRow>
          ) : null}
          {detail.customer_no_shows >= 2 ? (
            <DetailRow label="History">
              <NoShowTag count={detail.customer_no_shows} /> Consider calling to confirm their next visit.
            </DetailRow>
          ) : null}
          <DetailRow label="Booked at">{formatDateTime(detail.created_at)}</DetailRow>
          {detail.booking_id ? <BookingPanel bookingId={detail.booking_id} onChanged={() => load(true)} /> : null}

          <div className="de-callout de-mt-16">
            Change the status with the menu above. <strong>Service taken</strong> sends the feedback message and schedules the return-visit
            reminders. <strong>No-show</strong> sends the missed-appointment message, offering a new time. <strong>Booked</strong> undoes a mistake and schedules the reminders that are still
            ahead.
          </div>

          <div className="de-row de-row--wrap de-mt-16" style={{ gap: 10 }}>
            <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => openConversation(detail)}>
              {detail.source === 'sarvam' || detail.source === 'voice' ? '▶ Play recording' : '👁 View chat'}
            </button>
            {detail.status !== 'service_taken' ? (
              <button
                type="button"
                className="de-btn de-btn--primary de-btn--sm"
                disabled={lineBusy(busy, detail)}
                onClick={() => requestStatus(detail, 'service_taken')}
              >
                {lineBusy(busy, detail) ? <Spinner light /> : null}
                Mark service taken
              </button>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {pendingStatus ? (
        <Confirm
          title={`Change to ${STATUS_LABEL[pendingStatus.next]}?`}
          message={
            <div>
              <div>
                {`${pendingStatus.row.customer_name || 'Customer'} · ${pendingStatus.row.service_name}: ${STATUS_LABEL[pendingStatus.row.status]} → ${
                  STATUS_LABEL[pendingStatus.next]
                }. ${statusEffect(pendingStatus.row.status, pendingStatus.next)}`}
              </div>
              {pendingStatus.next === 'not_taken' ? (
                <label className="de-field de-mt-16">
                  <span className="de-label">Why (if they said)</span>
                  <select className="de-select" value={reason} onChange={(e) => setReason(e.target.value)}>
                    <option value="">Not known</option>
                    {Object.entries(REASONS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          }
          confirmLabel={`Mark ${STATUS_LABEL[pendingStatus.next]}`}
          danger={pendingStatus.next === 'no_show'}
          onConfirm={applyStatus}
          onCancel={() => setPendingStatus(null)}
        />
      ) : null}

      {booking ? (
        <BookingModal
          preset={booking}
          onClose={() => setBooking(null)}
          onBooked={() => load(true)}
          onSeveral={(prefill) => {
            setBooking(null);
            setMultiBooking(prefill);
          }}
        />
      ) : null}
      {multiBooking ? <MultiBookingModal prefill={multiBooking} onClose={() => setMultiBooking(null)} onBooked={() => load(true)} /> : null}
      {assignFor ? (
        <AssignStylistModal
          appointmentId={assignFor}
          onClose={() => setAssignFor(null)}
          onAssigned={() => {
            setDetail(null);
            load(true);
          }}
        />
      ) : null}

      {chatFor ? <ChatModal customerId={chatFor} channel={chatChannel} onClose={() => setChatFor(null)} /> : null}
      {recording ? <RecordingModal call={recording} onClose={() => setRecording(null)} /> : null}
    </div>
  );
}
